import React from "react";
import { Box, Button, Typography, useTheme } from "@mui/material";
import { AssessmentLeaderboardItem } from "../../../axios/quiz";
import { getAssessmentDurationStatDisplay } from "../../../utils/assessmentTimeLeft";
import {
  BarChart3,
  BookOpen,
  CheckCircle2,
  CircleHelp,
  Clock3,
  Flame,
  Info,
  ListChecks,
  Target,
  Trophy,
  Zap,
} from "lucide-react";
import { CustomChevronCircleIcon } from "../../../assets/icon";
import {
  actionsRowStyle,
  contestTopBarWrapStyle,
  detailCardStyle,
  detailGridStyle,
  footerNoteStyle,
  footerNoteIconStyle,
  heroCardStyle,
  heroSubtitleStyle,
  heroTextWrapStyle,
  heroTitleStyle,
  modalContainerStyle,
  modalInnerStyle,
  primaryButtonIconWrapStyle,
  primaryButtonStyle,
  ruleDescriptionStyle,
  ruleIconStyle,
  ruleRowStyle,
  ruleTitleStyle,
  rulesListStyle,
  secondaryButtonStyle,
  sectionTitleStyle,
  statCardStyle,
  statIconWrapStyle,
  statLabelStyle,
  statTextWrapStyle,
  statValueStyle,
  statsGridStyle,
  trophyRowStyle,
  trophyWrapStyle,
} from "./styles";
import { AssessmentLeaderboardCard } from "../assessment-leaderboard-card/assessment-leaderboard-card";
import { AssessmentContestTopBar } from "../assessment-contest-top-bar/assessment-contest-top-bar";
import useUserStore from "../../../stores/userStore";
import useAppStore from "../../../stores/appStore";

interface AssessmentWelcomeProps {
  totalQuestions: number;
  timeLimitSeconds: number;
  answeredQuestions?: number;
  currentUserPlace?: number | null;
  leaderboard?: AssessmentLeaderboardItem[];
  gamificationEnabled?: boolean;
  focusLabel?: string;
  contestTitle?: string | null;
  onStart: () => void;
  onClose: () => void;
  managerActions?: React.ReactNode;
}

interface StatCardProps {
  icon: React.ReactNode;
  value: string;
  label: React.ReactNode;
}

interface RuleRowProps {
  icon: React.ReactNode;
  title: string;
  description: string;
}

const StatCard = ({ icon, value, label }: StatCardProps) => (
  <Box sx={statCardStyle}>
    <Box sx={statIconWrapStyle}>{icon}</Box>
    <Box sx={statTextWrapStyle}>
      <Typography sx={statValueStyle}>{value}</Typography>
      {typeof label === "string" ? (
        <Typography sx={statLabelStyle}>{label}</Typography>
      ) : (
        label
      )}
    </Box>
  </Box>
);

const RuleRow = ({ icon, title, description }: RuleRowProps) => (
  <Box sx={ruleRowStyle}>
    <Box sx={ruleIconStyle}>{icon}</Box>
    <Box>
      <Typography sx={ruleTitleStyle}>{title}</Typography>
      <Typography sx={ruleDescriptionStyle}>{description}</Typography>
    </Box>
  </Box>
);

export const AssessmentWelcome = ({
  totalQuestions,
  timeLimitSeconds,
  answeredQuestions = 0,
  currentUserPlace,
  leaderboard = [],
  gamificationEnabled = true,
  focusLabel,
  contestTitle = null,
  onStart,
  onClose,
  managerActions,
}: AssessmentWelcomeProps) => {
  const theme = useTheme();
  const domain = useAppStore((state) => state.domain);
  const getFocusDisplayName = useUserStore((state) => state.getFocusDisplayName);
  const timeLimitDisplay = getAssessmentDurationStatDisplay(timeLimitSeconds);
  const isResume = answeredQuestions > 0;
  const challengeFocus = focusLabel?.trim()
    ? getFocusDisplayName(focusLabel.trim(), domain)
    : "Knowledge";

  return (
    <Box sx={modalContainerStyle}>
      <Box sx={modalInnerStyle}>
        <Box sx={contestTopBarWrapStyle}>
          <AssessmentContestTopBar
            contestTitle={contestTitle}
            currentUserPlace={currentUserPlace}
            leaderboard={leaderboard}
            actions={managerActions}
            gamificationEnabled={gamificationEnabled}
          />
        </Box>

        <Box sx={heroCardStyle}>
          <Box sx={trophyRowStyle}>
            <Box sx={trophyWrapStyle}>
              <Trophy size={38} />
            </Box>
          </Box>

          <Box sx={heroTextWrapStyle}>
            <Typography sx={heroTitleStyle}>
              {`Welcome to the ${challengeFocus} Knowledge Challenge`}
            </Typography>
            <Typography sx={heroSubtitleStyle}>
              {gamificationEnabled
                ? isResume
                  ? "Jump back in, answer fast, and keep climbing the leaderboard."
                  : "Compete with your peers, answer fast, and climb the leaderboard."
                : isResume
                  ? "Jump back in and keep answering to complete the challenge."
                  : "Answer the questions to complete the challenge."}
            </Typography>
            {isResume ? (
              <Typography sx={{ mt: 1.5, fontSize: "14px", fontWeight: 600, opacity: 0.72 }}>
                {`${answeredQuestions}/${totalQuestions} answered`}
              </Typography>
            ) : null}
          </Box>

          <Box sx={statsGridStyle()}>
            <StatCard
              icon={<CircleHelp size={20} />}
              value={String(totalQuestions)}
              label="Questions"
            />
            <StatCard
              icon={<Clock3 size={20} />}
              value={timeLimitDisplay.value}
              label={timeLimitDisplay.label}
            />
            {gamificationEnabled ? (
              <StatCard
                icon={<Zap size={20} />}
                value="Accuracy+"
                label="Speed bonus"
              />
            ) : (
              <StatCard
                icon={<Target size={20} />}
                value="Score"
                label="Points"
              />
            )}
            <StatCard
              icon={<BarChart3 size={20} />}
              value={gamificationEnabled ? "Live" : "Standard"}
              label={gamificationEnabled ? "Challenge" : "Contest"}
            />
          </Box>

          <Box sx={detailGridStyle}>
            <Box sx={detailCardStyle}>
              <Typography sx={sectionTitleStyle}>How scoring works</Typography>
              <Box sx={rulesListStyle}>
                <RuleRow
                  icon={<Target size={18} />}
                  title="Correct answer"
                  description="Earn base points for every correct answer."
                />
                {gamificationEnabled ? (
                  <>
                    <RuleRow
                      icon={<Zap size={18} />}
                      title="Speed bonus"
                      description="Answer faster to earn extra speed points."
                    />
                    <RuleRow
                      icon={<Flame size={18} />}
                      title="Streak bonus"
                      description="Build a streak of correct answers to earn bonus points."
                    />
                  </>
                ) : (
                  <RuleRow
                    icon={<CheckCircle2 size={18} />}
                    title="Final score"
                    description="Your total is based on correct answers across the contest."
                  />
                )}
              </Box>
            </Box>

            {gamificationEnabled ? (
              <AssessmentLeaderboardCard
                items={leaderboard}
                currentUserPlace={currentUserPlace}
                title="Preview leaderboard"
                emptyMessage="Leaderboard will appear after you start the challenge."
                maxItems={3}
                showExpandAction
              />
            ) : (
              <Box sx={detailCardStyle}>
                <Typography sx={sectionTitleStyle}>Tips for success</Typography>
                <Box sx={rulesListStyle}>
                  <RuleRow
                    icon={<BookOpen size={18} />}
                    title="Read carefully"
                    description="Take a moment to understand each question before answering."
                  />
                  <RuleRow
                    icon={<CheckCircle2 size={18} />}
                    title="Answer every question"
                    description="Complete all questions to finish the challenge and see your score."
                  />
                  <RuleRow
                    icon={<ListChecks size={18} />}
                    title="Manage your time"
                    description="Keep an eye on the timer and pace yourself across the challenge."
                  />
                </Box>
              </Box>
            )}
          </Box>

          <Box sx={actionsRowStyle}>
            <Button onClick={onStart} sx={primaryButtonStyle}>
              {isResume ? "Resume challenge" : "Start challenge"}
              <Box sx={primaryButtonIconWrapStyle}>
                <CustomChevronCircleIcon />
              </Box>
            </Button>
            <Button
              variant="outlined"
              onClick={onClose}
              sx={secondaryButtonStyle(theme)}
            >
              Close
            </Button>
          </Box>

          {gamificationEnabled ? (
            <Typography sx={footerNoteStyle}>
              <Box component="span" sx={footerNoteIconStyle}>
                <Info size={15} />
              </Box>
              Your rank will update in real time after each question.
            </Typography>
          ) : null}
        </Box>
      </Box>
    </Box>
  );
};

export default AssessmentWelcome;
